import type { components, paths } from '../../types/clients/game-session'
import type { ApiError, OpenapiError, UseInfiniteQueryReturn } from '../../types'
import { toValue, useDayjs, useInfiniteQuery, useOpenapi, useState } from '#imports'
import type { MaybeRefOrGetter } from '#imports'
import type { ManipulateType } from 'dayjs'

export const GAME_SESSION_BASE_URL = '/gateway/game-session'
export const END_MULTIPLE_PATH = '/public/game-sessions/end-multiple'

type GameSessionsPage = components['schemas']['PublicGameSessionsData'] | undefined
type GameRoundsPage = components['schemas']['PublicGameRoundsData'] | undefined

/**
 * @title useGameSessionClient
 * @see Docs https://game-session-prod.spilnu.dk/docs/api
 */
export const useGameSessionClient = () => {
    const client = useOpenapi<paths>({
        baseUrl: GAME_SESSION_BASE_URL,
        service: 'gameSession'
    })

    const pingClient = useOpenapi<paths>({
        baseUrl: GAME_SESSION_BASE_URL,
        service: 'gameSession',
        reportErrors: false
    })
    const dayjs = useDayjs()

    // The account history intervals are lookback strings ("3 months"); game-session takes an absolute `from` (UTC with Z) instead.
    function lookbackStart(interval: string): string {
        const match = /^(\d+) ([a-z]+)$/.exec(interval)

        if (!match) throw new Error(`Cannot turn the history interval "${interval}" into a timestamp`)

        return dayjs().subtract(Number(match[1]), match[2] as ManipulateType).toISOString()
    }

    async function launchGame(body: components['schemas']['LaunchRequest']) {
        const { data } = await client.POST('/public/launch-game', { body })

        return data
    }

    async function pingGameSession(body: components['schemas']['PingRequest']) {
        await pingClient.POST('/public/game-sessions/ping', { body })
    }

    async function getNextBingoRound(slug: string, signal?: AbortSignal) {
        const { data } = await client.GET('/public/bingo/next-round', { params: { query: { slug } }, signal })

        return data
    }

    async function endGameSessions(body: components['schemas']['EndMultipleGameSessionsRequest']) {
        // keepalive: the close must survive an unload racing it
        await client.POST(END_MULTIPLE_PATH, { body, keepalive: true })
    }

    async function getRecentGames(brand: components['schemas']['Brand'], signal?: AbortSignal) {
        const { data } = await client.GET('/public/game-sessions/recent-games', { params: { query: { brand } }, signal })

        return data?.games ?? []
    }

    async function releaseGameInstances(gameInstanceIds: string[]) {
        const { data } = await client.POST('/public/game-sessions/release-instances', { body: { gameInstanceIds } })

        return data?.unreleasedInstanceIds ?? gameInstanceIds
    }

    async function getRealityCheck() {
        const { data } = await client.GET('/public/game-sessions/reality-check')

        return data
    }

    function useGetGameSessions(interval: MaybeRefOrGetter<string>): UseInfiniteQueryReturn<GameSessionsPage, ApiError<GameSessionsPage | OpenapiError<GameSessionsPage> | Error | null>> {
        // Set when page 1 is fetched and kept in the payload, so later pages send the same `from` after hydration, a cached remount
        // or a timezone change: the lookback runs in local time, and a DST switch inside it moves the instant between server and browser
        const windowStarts = useState<Record<string, string>>('game-sessions-window-starts', () => ({}))

        return useInfiniteQuery(async ({ pageParam, signal }) => {
            const lookback = toValue(interval)
            const stored = windowStarts.value[lookback]
            const from = pageParam === 1 || !stored ? lookbackStart(lookback) : stored

            windowStarts.value[lookback] = from

            const { data } = await client.GET('/public/game-sessions', {
                signal,
                params: { query: { from, page: pageParam, perPage: 10 } }
            })

            return data
        }, {
            key: () => `get-game-sessions:${toValue(interval).replace(/ /g, '-')}`,
            initialPageParam: 1,
            getNextPageParam({ currentPage }) {
                if (!currentPage?.data) return

                return currentPage.data.currentPage < currentPage.data.lastPage ? currentPage.data.currentPage + 1 : undefined
            },
            getPreviousPageParam({ currentPage }) {
                if (!currentPage?.data) return

                return currentPage.data.currentPage > 1 ? currentPage.data.currentPage - 1 : undefined
            },
        })
    }

    function useGetGameSessionRounds(id: MaybeRefOrGetter<string>, enabled: MaybeRefOrGetter<boolean>): UseInfiniteQueryReturn<GameRoundsPage, ApiError<GameRoundsPage | OpenapiError<GameRoundsPage> | Error | null>> {
        return useInfiniteQuery(async ({ pageParam, signal }) => {
            const { data } = await client.GET('/public/game-sessions/{id}/rounds', {
                signal,
                params: { path: { id: toValue(id) }, query: { page: pageParam, perPage: 10 } }
            })

            return data
        }, {
            enabled,
            key: () => `get-game-session-rounds:${toValue(id)}`,
            initialPageParam: 1,
            getNextPageParam({ currentPage }) {
                if (!currentPage?.data) return

                return currentPage.data.currentPage < currentPage.data.lastPage ? currentPage.data.currentPage + 1 : undefined
            },
            getPreviousPageParam({ currentPage }) {
                if (!currentPage?.data) return

                return currentPage.data.currentPage > 1 ? currentPage.data.currentPage - 1 : undefined
            },
        })
    }

    return {
        client,
        // For the unload beacon, which cannot go through the client
        endMultipleUrl: `${GAME_SESSION_BASE_URL}${END_MULTIPLE_PATH}`,

        launchGame,
        pingGameSession,
        getNextBingoRound,
        endGameSessions,
        getRecentGames,
        releaseGameInstances,
        getRealityCheck,
        useGetGameSessions,
        useGetGameSessionRounds
    }
}
